import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Cancel, SearchIcon } from "../../../../components/styled/AllImages";
import {
  Box,
  ButtonStyled,
  HeadingStyled,
  SpanStyled,
} from "../../../../components/styled/Elements";
import { useTranslation } from "react-i18next";
import { CustomCheckbox } from "../../../../components/styled/FormElements";
import styled from "styled-components";
import FloatingInput from "../../../../components/common/inputFields/FloatingInput";

const MultiUsersModal = ({
  show,
  onHide,
  branches = [],
  departments = [],
  users = [],
  checkedBranchIds = [],
  checkedDepartmentIds = [],
  selectedUsers,
  setSelectedUsers,
  setSelections,
  onUsersSelected,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);

  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [localSelectedUsers, setLocalSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (show) {
      setStep(1);
      setSelectedBranches([]);
      setSelectedDepartments([]);
      setLocalSelectedUsers(selectedUsers || []);
      setSearchTerm("");
    }
  }, [show]);

  useEffect(() => {
  if (show) {
    setStep(1);
    setSearchTerm("");

    const matchedBranches = branches.filter((b) =>
      checkedBranchIds.includes(b.id)
    );
    setSelectedBranches(matchedBranches);

    const matchedDepartments = departments.filter((d) =>
      checkedDepartmentIds.includes(d.id)
    );
    setSelectedDepartments(matchedDepartments);

    const matchedUsers = users.filter(
      (u) =>
        u.user_detail &&
        checkedDepartmentIds.includes(u.user_detail.department_id)
    );

    setLocalSelectedUsers(selectedUsers);
  }
}, [show, branches, departments, users, checkedBranchIds, checkedDepartmentIds]);

  

  const handleCheckboxChange = (item, selectedList, setSelectedList) => {
    if (selectedList.some((i) => i.id === item.id)) {
      setSelectedList(selectedList.filter((i) => i.id !== item.id));
    } else {
      setSelectedList([...selectedList, { ...item, shareReferrals: false, referralNote: "", referralUniqueKey: `other_department_${item.id}` }]);
    }
  };

  const handleNext = () => {
    if (step === 1 && selectedBranches.length > 0) setStep(2);
    else if (step === 2 && selectedDepartments.length > 0) setStep(3);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleAdd = () => {
    const branchIds = selectedBranches.map((b) => b.id);
    const departmentIds = selectedDepartments.map((d) => d.id);
    const userIds = localSelectedUsers.map((u) => u.id);

    if (typeof onUsersSelected === "function") {
      onUsersSelected(localSelectedUsers);
    } else if (typeof setSelectedUsers === "function") {
      setSelectedUsers(localSelectedUsers);
    }

    if (typeof setSelections === "function") {
      setSelections({
        branch_ids: branchIds,
        department_ids: departmentIds,
        user_ids: userIds,
      });
    }

    onHide();
  };

  const getFilteredList = () => {
    let list = [];

    if (step === 1) {
      list = branches;
    } else if (step === 2) {
      list = departments.filter((d) =>
        selectedBranches.some((b) => b.id === d.location_id)
      );
    } else {
      list = users.filter(
        (u) =>
          u.user_detail &&
          selectedDepartments.some((d) => d.id === u.user_detail.department_id)
      );
    }

    return list.filter((item) =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const renderStepTitle = () => {
    if (step === 1) return t("Select Branches");
    if (step === 2) return t("Select Departments");
    return t("Select Users");
  };

  const getSelectionList = () => {
    return step === 1
      ? selectedBranches
      : step === 2
      ? selectedDepartments
      : localSelectedUsers;
  };

  const setSelectionList = (list) => {
    if (step === 1) setSelectedBranches(list);
    else if (step === 2) setSelectedDepartments(list);
    else setLocalSelectedUsers(list);
  };

  // Toggle "Share Referrals" for a specific user
  const handleToggleShare = (uKey) => {
    setSelectionList((prev) =>
      prev.map((user) =>
        user.referralUniqueKey === uKey ? { ...user, shareReferrals: !user.shareReferrals } : user
      )
    );
  };

  // Update Note for a specific user
  const handleNoteChange = (uKey, value) => {
    setSelectionList((prev) =>
      prev.map((user) =>
        user.referralUniqueKey === uKey ? { ...user, referralNote: value } : user
      )
    );
  };

  return (
    <Modal size="md" show={show} onHide={onHide} centered>
      <Box className="d-flex justify-content-between align-items-center p-3">
        <HeadingStyled size="24px" color="var(--text)">
          {renderStepTitle()}
        </HeadingStyled>
        <span className="close-button cursor-pointer" onClick={onHide}>
          <Cancel />
        </span>
      </Box>

      <Box className="d-flex p-3 pt-0 border-bottom">
        <FloatingInput
          name="search"
          type="input"
          fieldType="text"
          label={t("search")}
          placeholder={t("search")}
          icon={SearchIcon}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>

      <ScrollableContainer>
        {getFilteredList().map((item) => {
          const uKey = `other_department_${item.id}`
          const isChecked = getSelectionList().some((i) => i.id === item.id || i.referralUniqueKey === uKey);
          const currentUserData = getSelectionList().find((u) => u.referralUniqueKey === uKey);

          return (
            <MultipleDepartmentsRow key={item.id}>
              <Box className="userDataContainer">
                <Box className="userDataInfo">
                  <Box className="userDataNameRole">
                    <SpanStyled color="var(--text)" size="16px" weight="bold">
                      {item.name}
                    </SpanStyled>
                    {item.role && (
                      <SpanStyled color="var(--text2)" size="14px">
                        {item.role}
                      </SpanStyled>
                    )}
                    {item.subLabel && (
                      <SpanStyled color="var(--text2)" size="14px">
                        {item.subLabel}
                      </SpanStyled>
                    )}
                  </Box>
                </Box>
                <Box className="userDataCheckBox">
                  <CustomCheckbox
                    name="item"
                    checked={isChecked}
                    onChange={() => {
                      handleCheckboxChange(
                        item,
                        getSelectionList(),
                        setSelectionList
                      )
                    }}
                  />
                </Box>
              </Box>

              {(step === 3 && isChecked) && (
                <Box className="w-100 p-2 border-top mt-1" style={{ background: 'var(--bgSecondary)', borderRadius: '8px' }}>
                  <Box className="d-flex align-items-center gap-2 mb-2">
                    <CustomCheckbox
                      name={`share_${item.id}`}
                      checked={currentUserData?.shareReferrals || false}
                      onChange={() => handleToggleShare(uKey)}
                    />
                    <SpanStyled color="var(--text)" size="14px">
                      {t("Do you want to share referrals with this user?")}
                    </SpanStyled>
                  </Box>

                  {currentUserData?.shareReferrals && (
                    <Box className="mt-2">
                      <textarea
                        className="form-control"
                        placeholder={t("Enter referral details...")}
                        rows={2}
                        style={{ fontSize: '14px', borderRadius: '6px' }}
                        value={currentUserData?.referralNote || ""}
                        onChange={(e) => handleNoteChange(uKey, e.target.value)}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </MultipleDepartmentsRow>
          )
        })}
      </ScrollableContainer>

      <div className="modal-footer">
        <ButtonStyled
          label={t("Back")}
          onClick={handleBack}
          bg="var(--bgSecondary)"
          color="var(--text2)"
          disabled={step === 1}
        />
        {step < 3 ? (
          <ButtonStyled
            bg="var(--themeColor)"
            label={t("Next")}
            onClick={handleNext}
            disabled={
              (step === 1 && selectedBranches.length === 0) ||
              (step === 2 && selectedDepartments.length === 0)
            }
          />
        ) : (
          <ButtonStyled
            bg="var(--themeColor)"
            label={t("Add")}
            onClick={handleAdd}
            disabled={localSelectedUsers.length === 0}
          />
        )}
      </div>
    </Modal>
  );
};

export default MultiUsersModal;

const ScrollableContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 10px;
  border-radius: 12px;
  padding: 8px;

  &::-webkit-scrollbar {
    width: 7px;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-track {
    background: var(--bgSecondary);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #e4e8ef;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #b8c0cc;
  }
`;

const MultipleDepartmentsRow = styled.div`
  padding: 1rem;
  border-bottom: 1px solid var(--borderLight);
  width: 100%;
  cursor: pointer;

  .userDataContainer {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
  }

  .userDataInfo {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .userDataNameRole {
    display: flex;
    flex-direction: column;
  }
`;
